/**
 * ReviewHeaderReadout - where the session stands, in the page header.
 *
 * This was a whole bar of its own: a full-width strip under the breadcrumbs
 * carrying a 20px risk number, four severity dots, a three-bucket progress
 * line and a stray pane rail. It cost a row of vertical space on every screen
 * of a page whose entire job is reading a document beside a finding - and the
 * facts on it are glanceable, not workable. Nothing on it was ever clicked.
 *
 * So it is compact and it sits in the header, beside the collaborators: risk,
 * severity, progress. Three readings, in the order a reviewer asks them —
 * how bad is this, of what, and how far am I.
 *
 * PROGRESS IS THREE BUCKETS, not two. `decided` excludes escalated (see
 * `decisionBuckets`), so the bar cannot read full while findings are waiting
 * on someone; escalated has its own segment and its own colour between the
 * decided fill and the empty remainder.
 */

import React from 'react';
import clsx from 'clsx';
import { DecisionBuckets } from './decisionBuckets';

interface Props {
  /** 0–10. Null when the session has not scored one. */
  riskScore?: number | null;
  severity: {
    critical: number;
    major: number;
    minor: number;
    info: number;
  };
  buckets: DecisionBuckets;
  /** Already-translated - this component holds no copy of its own. */
  labels: {
    risk: string;
    decided: string;
    escalated: string;
    pending: string;
    critical: string;
    major: string;
    minor: string;
    info: string;
  };
}

const DIVIDER = 'h-5 w-px bg-hairline flex-shrink-0';

/** One severity tally. Absent severities draw nothing rather than a zero. */
const SeverityDot: React.FC<{ count: number; tone: string; label: string }> = ({
  count, tone, label,
}) => {
  if (!count) return null;
  return (
    <span className="flex items-center gap-1" title={`${count} ${label}`}>
      <span className={clsx('w-1.5 h-1.5 rounded-full', tone)} aria-hidden />
      <span className="text-[11px] text-fg-body tabular-nums">{count}</span>
    </span>
  );
};

const ReviewHeaderReadout: React.FC<Props> = ({
  riskScore, severity, buckets, labels,
}) => {
  const hasRisk = riskScore !== null && riskScore !== undefined;
  const hasSeverity = severity.critical + severity.major + severity.minor + severity.info > 0;

  return (
    <div
      className="flex items-center gap-3 min-w-0"
      data-tour-id="review-header-readout"
      data-testid="review-header-readout"
    >
      {hasRisk && (
        <span className="flex items-baseline gap-1 flex-shrink-0" data-testid="review-risk-score">
          <span className={clsx(
            'text-[15px] font-semibold tabular-nums leading-none',
            (riskScore ?? 0) >= 7 ? 'text-danger-600 dark:text-danger-400'
              : (riskScore ?? 0) >= 4 ? 'text-warning-600 dark:text-warning-400'
              : 'text-success-600 dark:text-success-400',
          )}>
            {Number(riskScore).toFixed(1)}
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
            {labels.risk}
          </span>
        </span>
      )}

      {hasRisk && hasSeverity && <span className={clsx(DIVIDER, 'hidden lg:block')} aria-hidden />}

      {/* Severity and progress step out of the way before the risk figure
          does - a bar too narrow for all three should still say how bad it
          is. Dropping them is how this stays inside its column instead of
          overflowing onto the document's name. */}
      {hasSeverity && (
        <span className="hidden lg:flex items-center gap-2 flex-shrink-0">
          <SeverityDot count={severity.critical} tone="bg-danger-500" label={labels.critical} />
          <SeverityDot count={severity.major} tone="bg-warning-400" label={labels.major} />
          <SeverityDot count={severity.minor} tone="bg-warning-400" label={labels.minor} />
          <SeverityDot count={severity.info} tone="bg-info-400" label={labels.info} />
        </span>
      )}

      {(hasRisk || hasSeverity) && buckets.total > 0 && (
        <span className={clsx(DIVIDER, 'hidden xl:block')} aria-hidden />
      )}

      {buckets.total > 0 && (
        <span className="hidden xl:flex items-center gap-2 flex-shrink-0" data-testid="review-progress">
          <span className="text-[11px] text-fg-muted tabular-nums">
            <span className="font-medium text-success-600 dark:text-success-400">
              {buckets.decided}
            </span>{' '}
            {labels.decided}
            {buckets.escalated > 0 && (
              <>
                <span className="mx-1 text-fg-muted/60" aria-hidden>·</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {buckets.escalated}
                </span>{' '}
                {labels.escalated}
              </>
            )}
            <span className="mx-1 text-fg-muted/60" aria-hidden>·</span>
            {buckets.pending} {labels.pending}
          </span>
          <span className="w-14 h-1.5 bg-surface-sunken rounded-full overflow-hidden flex">
            <span
              className="h-full bg-success-400 transition-all duration-300"
              style={{ width: `${(buckets.decided / buckets.total) * 100}%` }}
            />
            <span
              className="h-full bg-purple-400 transition-all duration-300"
              style={{ width: `${(buckets.escalated / buckets.total) * 100}%` }}
            />
          </span>
        </span>
      )}
    </div>
  );
};

export default ReviewHeaderReadout;
